<template>
  <div class="login-container">
    <div class="login-card">
      <div class="logo-circle">
        <img src="@/assets/logo.png" alt="Logo" class="logo-image">
      </div>

      <h1 class="login-title">BDE Anmelden</h1>

      <div class="login-form">
        <div class="form-group">
          <label for="email" class="form-label">E-Mail</label>
          <input
              type="email"
              class="form-input"
              id="email"
              v-model="credentials.email"
              placeholder="name@beispiel.de"
              autocomplete="email"
              @keyup.enter="handleLogin"
          >
        </div>

        <div class="form-group">
          <label for="password" class="form-label">Passwort</label>
          <div class="input-wrapper">
            <input
                :type="showPassword ? 'text' : 'password'"
                class="form-input"
                id="password"
                v-model="credentials.password"
                placeholder="••••••••"
                autocomplete="current-password"
                @keyup.enter="handleLogin"
            >
            <button
                type="button"
                class="password-toggle"
                @click="showPassword = !showPassword"
                tabindex="-1"
            >
              <i :class="showPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
            </button>
          </div>
        </div>

        <div v-if="loginError" class="error-message">
          {{ loginError }}
        </div>

        <button
            type="button"
            class="btn-login"
            :disabled="loading"
            @click="handleLogin"
        >
          <span v-if="loading" class="spinner"></span>
          {{ loading ? 'Anmelden...' : 'Anmelden' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'

export default {
  name: 'Login',
  data() {
    return {
      credentials: {
        email: '',
        password: '',
        remember: false
      },
      showPassword: false,
      loginError: null
    }
  },

  computed: {
    ...mapGetters(['loading', 'error', 'isAuthenticated'])
  },

  watch: {
    error: {
      handler(newError) {
        // Store error locally so it persists even if store clears it
        if (newError) {
          this.loginError = newError
        }
      },
      immediate: false
    }
  },

  created() {
    // Redirect if already logged in
    if (this.isAuthenticated) {
      this.$router.push(this.$route.query.redirect || '/')
    }
  },

  methods: {
    ...mapActions(['login']),

    async handleLogin() {
      // Clear previous error ONLY when starting new attempt
      this.loginError = null

      try {
        const success = await this.login(this.credentials)

        if (success) {
          // Login successful - redirect
          const redirectPath = this.$route.query.redirect || '/auftraege'
          this.$router.push(redirectPath)
        } else {
          // Login failed - error should be set by watcher from store
          // Wait a tick for watcher to process
          await this.$nextTick()

          // If still no error after watcher, set generic one
          if (!this.loginError) {
            this.loginError = 'Anmeldung fehlgeschlagen. Bitte überprüfen Sie Ihre E-Mail und Ihr Passwort.'
          }

        }
      } catch (error) {
        // Catch ANY error and store it permanently in component state
        console.error('🔴 EXCEPTION CAUGHT:', error)

        // Extract error message
        let errorMsg = 'Ein Fehler ist aufgetreten'

        if (error.response && error.response.data) {
          errorMsg = error.response.data.error || error.response.data.message || JSON.stringify(error.response.data)
        } else if (error.message) {
          errorMsg = error.message
        } else if (typeof error === 'string') {
          errorMsg = error
        }

        this.loginError = errorMsg
      }
    }
  }
}
</script>

<style scoped>
.login-container {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #ffffff;
  padding: 20px;
  font-family: Helvetica, Arial, sans-serif;
}

.login-card {
  width: 100%;
  max-width: 360px;
  padding: 40px 24px;
}

.logo-circle {
  width: 80px;
  height: 80px;
  margin: 0 auto 24px;
  border-radius: 50%;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #ffffff;
}

.logo-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.login-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #000000;
  margin: 0 0 32px 0;
  text-align: center;
}

.login-form {
  width: 100%;
}

.form-group {
  margin-bottom: 20px;
}

.form-label {
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  color: #000000;
  margin-bottom: 6px;
}

.input-wrapper {
  position: relative;
}

.form-input {
  width: 100%;
  padding: 10px 12px;
  font-size: 0.9rem;
  color: #000000;
  background: #ffffff;
  border: 1px solid #cccccc;
  border-radius: 4px;
  outline: none;
}

.form-input:focus {
  border-color: #667eea;
}

.form-input::placeholder {
  color: #bbbbbb;
}

.password-toggle {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #999999;
  cursor: pointer;
  padding: 4px;
}

.password-toggle:hover {
  color: #667eea;
}

.password-toggle i {
  font-size: 0.875rem;
}

.error-message {
  background: #ffffff;
  margin-bottom: 20px;
  color: #ff0000;
  font-size: 0.875rem;
  line-height: 1.4;
  word-break: break-word;
  user-select: text;
}

.btn-login {
  width: 100%;
  padding: 11px;
  font-size: 0.9rem;
  font-weight: 600;
  color: #ffffff;
  background: #667eea;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.btn-login:hover:not(:disabled) {
  background: #5568d3;
}

.btn-login:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.spinner {
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: #ffffff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

@media (max-width: 400px) {
  .login-card {
    padding: 32px 20px;
  }

  .login-title {
    font-size: 1.375rem;
  }
}
</style>
